import React from 'react';
import { Link } from 'react-router-dom';

/** Fixed legal effective date — update intentionally when policy changes. */
const LAST_UPDATED = 'May 4, 2026';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <h2>1. Introduction</h2>
            <p>
              This Privacy Policy describes how <strong>Devello Tech</strong> (&quot;Devello Tech,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;) collects, uses, discloses, and protects personal information in
              connection with our website at <strong>https://devellotech.ca</strong>, our web application, our hosted API
              and backend services, and our mobile applications for iOS and Android (together, the
              &quot;Service&quot;). By using the Service, you agree to this policy. If you do not agree, please
              discontinue use of the Service.
            </p>
            <p>
              This policy is designed to meet common expectations for business software in Canada (including
              PIPEDA, where it applies) and to address other regions where our users may be located. It is not a
              substitute for legal advice for your own business compliance.
            </p>

            <h2>2. Who is responsible for your information</h2>
            <p>
              Devello Tech is the organization responsible for personal information described in this policy, except
              where we act strictly as a processor on behalf of your organization under a separate agreement. If you are
              an individual using the Service on behalf of a company, that company is often the &quot;business&quot; for
              employee and client data you enter; we process that data to provide the Service to you.
            </p>

            <h2>3. Information we collect</h2>
            <h3>3.1 You provide</h3>
            <ul>
              <li>
                <strong>Account and profile</strong>: name, email address, password (stored in hashed form by our
                identity provider), company name, phone, and similar profile or settings fields.
              </li>
              <li>
                <strong>Business and project data</strong>: information you add about clients, jobs or projects, invoices,
                expenses, team members, schedules, files, photos, notes, and other project or CRM content.
              </li>
              <li>
                <strong>Billing and subscription</strong>: plan selection, billing address, and payment-related metadata.
                Card numbers and full payment instrument details are processed by <strong>Stripe</strong> when you
                subscribe on devellotech.ca; they are not stored on our servers in full.
              </li>
              <li>
                <strong>Support and communications</strong>: messages you send to us, including contact form
                submissions, support tickets, and email.
              </li>
            </ul>

            <h3>3.2 Collected automatically</h3>
            <ul>
              <li>
                <strong>Usage and device data</strong>: IP address, browser or app version, device type, general
                location derived from IP, timestamps, pages or screens viewed, and similar diagnostic data.
              </li>
              <li>
                <strong>Logs and performance</strong>: server, application, and error logs, including stack traces and
                request metadata, which may be shared with our error and performance monitoring provider when enabled.
              </li>
              <li>
                <strong>Authentication state</strong>: session tokens and cookies or local storage needed to keep you
                signed in (for example in the browser or mobile secure storage).
              </li>
            </ul>

            <h3>3.3 Mobile app permissions</h3>
            <p>Our mobile app may request access to:</p>
            <ul>
              <li>
                <strong>Camera</strong> — to photograph receipts or job-site images where you use those features.
              </li>
              <li>
                <strong>Photo library and file/document access</strong> — may be used to select project photos, receipt
                images, PDFs, permits, and other project documents when you choose to upload them.
              </li>
              <li>
                <strong>Local storage</strong> — to cache data and session information on your device for performance
                and offline resilience, as implemented in the app.
              </li>
            </ul>
            <p>You can revoke permissions through your device settings; some features may not work without them.</p>

            <h3>3.4 Information from third parties</h3>
            <ul>
              <li>
                <strong>Stripe</strong>: subscription status, payment events, and customer identifiers when you pay on
                our website.
              </li>
              <li>
                <strong>Material pricing searches</strong>: when you use material price search (where available), our
                backend may send your query and approximate location or region to <strong>SerpAPI</strong> (or similar
                providers) to retrieve public pricing-related results. Those providers process queries under their own
                policies.
              </li>
            </ul>

            <h2>4. How we use personal information</h2>
            <p>We use personal information to:</p>
            <ul>
              <li>Create and secure accounts; authenticate users</li>
              <li>Operate, deliver, and improve the Service (including syncing data between web and mobile)</li>
              <li>Process subscriptions and entitlements (including Pro-only mobile access)</li>
              <li>
                Generate invoice PDFs in your <strong>browser</strong> on the website (files stay on your device unless
                you download or share them). Store uploads — including on the <strong>mobile app</strong> (project photos,
                vault files, receipts, company logos) — in <strong>Supabase Storage</strong>. The mobile app does not
                generate invoice PDFs; that is done on the website
              </li>
              <li>Send transactional and service messages (for example security alerts, billing receipts, product
                notices)</li>
              <li>Provide support and respond to inquiries</li>
              <li>Detect, prevent, and investigate abuse, fraud, and security incidents</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Where permitted by law and your preferences, send optional product updates or marketing (you may opt
                out of marketing as described below)</li>
            </ul>

            <h2>5. Legal bases (summary)</h2>
            <p>Depending on your region and the type of data, we rely on one or more of:</p>
            <ul>
              <li><strong>Performance of a contract</strong> — providing the Service you requested</li>
              <li><strong>Legitimate interests</strong> — securing and improving the Service, analytics at an aggregate
                level, and fraud prevention, balanced against your rights</li>
              <li><strong>Consent</strong> — where required for cookies beyond strictly necessary, marketing emails, or
                certain sensitive processing</li>
              <li><strong>Legal obligation</strong> — records we must keep by law</li>
            </ul>

            <h2>6. How we share information</h2>
            <h3>6.1 Service providers (processors)</h3>
            <p>
              We share information with vendors who host infrastructure or provide services on our behalf. Categories
              include:
            </p>
            <ul>
              <li>
                <strong>Supabase</strong> — authentication, PostgreSQL database, <strong>Supabase Storage</strong> for
                user uploads (for example the <code>project-files</code> and <code>company-logos</code> buckets), and
                related hosted infrastructure
              </li>
              <li>
                <strong>Render</strong> (or comparable hosting) — hosting our API and backend application server
              </li>
              <li>
                <strong>Stripe</strong> — payment processing for subscriptions purchased on devellotech.ca
              </li>
              <li>
                <strong>Apple and Google</strong> — distribution of our mobile app through the App Store and Google Play
                (you install the app through them; paid plans are not purchased via in-app billing)
              </li>
              <li>
                <strong>SendGrid</strong> (or similar) — transactional email delivery from our backend
              </li>
              <li>
                <strong>EmailJS</strong> (or similar) — website contact form delivery
              </li>
              <li>
                <strong>SerpAPI</strong> — material pricing related queries when you use that feature
              </li>
              <li>
                <strong>Sentry</strong> — error monitoring and session replay (with masking configured where applicable)
                when enabled in our environments
              </li>
            </ul>
            <p>
              We contractually require service providers to protect personal information and use it only for the services
              they provide to us, subject to their public documentation and applicable law.
            </p>

            <h3>6.2 Legal and safety</h3>
            <p>We may disclose information if we believe in good faith that disclosure is required to:</p>
            <ul>
              <li>Comply with law, regulation, legal process, or governmental request</li>
              <li>Protect the rights, property, or safety of Devello Tech, our users, or the public</li>
              <li>Detect or prevent fraud or security issues</li>
            </ul>

            <h3>6.3 Business transfers</h3>
            <p>
              If we are involved in a merger, acquisition, financing, or sale of assets, personal information may be
              transferred as part of that transaction. We will require the successor to honor this policy or notify you
              of changes as required by law.
            </p>

            <h3>6.4 We do not sell personal information</h3>
            <p>
              We do not sell your personal information for monetary consideration as &quot;sale&quot; is commonly
              understood under U.S. state privacy laws. We do not share personal information with third parties for their
              independent marketing without your consent.
            </p>

            <h2>7. International transfers</h2>
            <p>
              We operate from Canada, but our service providers may process or store data in Canada, the United States,
              the European Union, or other regions. Where required, we use appropriate safeguards such as contractual
              clauses or vendor certifications recognized under applicable law for cross-border transfers.
            </p>

            <h2>8. Retention</h2>
            <p>We retain personal information as long as needed to:</p>
            <ul>
              <li>Provide the Service and maintain your account</li>
              <li>Meet legal, tax, and accounting requirements (which may require retaining certain records for several
                years)</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>
            <p>
              When retention periods end, we delete or anonymize information where feasible. Some residual copies may
              persist in backups for a limited period until those backups are rotated.
            </p>

            <h2>9. Security</h2>
            <p>
              We implement administrative, technical, and organizational measures appropriate to the nature of the Service,
              including encryption in transit for connections using TLS where configured, access controls, and vendor
              security practices. No method of transmission or storage is completely secure; you should protect your
              credentials and devices.
            </p>

            <h2>10. Your rights and choices</h2>
            <p>Depending on where you live, you may have rights to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion, subject to legal exceptions</li>
              <li>Object to or restrict certain processing</li>
              <li>Withdraw consent where processing was consent-based</li>
              <li>Export your business data where the product provides export tools</li>
              <li>Opt out of marketing emails using the unsubscribe link or by contacting us</li>
            </ul>
            <p>
              To exercise rights, contact us at the emails below. We may need to verify your identity. If you are in the
              EU or UK, you may lodge a complaint with your supervisory authority. Canadian residents may contact the
              Office of the Privacy Commissioner of Canada or applicable provincial commissioner for concerns we cannot
              resolve.
            </p>

            <h2>11. Cookies and similar technologies</h2>
            <p>
              Our website and dashboard may use cookies and similar technologies for session management, preferences,
              security, and (where enabled) analytics or performance measurement. You can control cookies through your
              browser; blocking essential cookies may affect login or functionality.
            </p>

            <h2>12. Children</h2>
            <p>
              The Service is not directed to children for personal, non-business use. We do not knowingly collect personal
              information from anyone under the age of majority for contracting in their jurisdiction for business
              accounts. If you believe we have collected such information, contact us and we will take appropriate steps
              to delete it.
            </p>

            <h2>13. Automated decision-making</h2>
            <p>
              We do not use personal information for solely automated decisions that produce legal or similarly
              significant effects on individuals without human involvement, except as needed for routine fraud or abuse
              prevention.
            </p>

            <h2>14. California residents (CCPA / CPRA)</h2>
            <p>If California law applies to you, you may have the rights described in California privacy laws, including:</p>
            <ul>
              <li>Right to know categories and specific pieces of personal information collected</li>
              <li>Right to delete personal information, subject to exceptions</li>
              <li>Right to correct inaccurate personal information</li>
              <li>Right to opt out of &quot;sale&quot; or &quot;sharing&quot; (we do not sell personal information as
                described in Section 6.4; certain analytics cookies may constitute &quot;sharing&quot; under California
                law if we enable them — contact us to exercise opt-out rights)</li>
              <li>Right to limit use of sensitive personal information if applicable</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p>Authorized agents may submit requests with proof of authorization where permitted by law.</p>

            <h2>15. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the new version on this page and revise
              the &quot;Last updated&quot; date. Where changes are material and law requires it, we will provide
              additional notice (for example by email or in-product message).
            </p>

            <h2>16. Contact us</h2>
            <p>For privacy questions or requests:</p>
            <div className="bg-gray-50 p-4 rounded-lg not-prose text-gray-900">
              <p className="font-semibold">Devello Tech</p>
              <p>Email (all inquiries, including privacy and support): info@devellotech.ca</p>
              <p>Website: https://devellotech.ca</p>
              <p>Address: Guelph, Ontario, Canada</p>
              <p className="text-sm text-gray-600 mt-2">
                Please include &quot;Privacy Inquiry&quot; in the subject line for privacy-related email.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
